import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";
import ScaleIcon from "@mui/icons-material/Scale";

const Keypad = ({ value = "0", onChange, onSubmit, onClear }) => {
  const [accumulator, setAccumulator] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  // น้ำหนักจากเครื่องชั่ง
  const [weight, setWeight] = useState("0");
  const lastWeightRef = useRef(null);
  const stableStartTimeRef = useRef(null);

  // ---- WebSocket: อ่านค่าน้ำหนักแบบเรียลไทม์ ----
  const handleData = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.weight != null) {
        const raw = Number(data.weight);
        const rounded = Number(raw.toFixed(3));

        if (
          lastWeightRef.current === null ||
          Math.abs(rounded - lastWeightRef.current) > 0.05
        ) {
          lastWeightRef.current = rounded;
          stableStartTimeRef.current = Date.now();
        }
        setWeight(rounded.toFixed(3));
      }
    } catch (e) {
      // ignore parse error
    }
  };

  useEffect(() => {
    const socket = new WebSocket("ws://localhost:3001/ws");
    socket.onmessage = handleData;

    // interval ตัวอย่าง (ถ้าจะใช้เช็คความนิ่งของค่าน้ำหนัก)
    const interval = setInterval(() => {
      if (lastWeightRef.current !== null && stableStartTimeRef.current !== null) {
        const elapsed = Date.now() - stableStartTimeRef.current;
        console.log('elapsed',elapsed);
        // ใช้ elapsed ตัดสินว่าค่านิ่งพอหรือยังได้
      }
    }, 200);

    return () => {
      try { socket.close(); } catch {}
      clearInterval(interval);
    };
  }, []);

  // ---- คีย์บอร์ด: 0-9 . Backspace Delete Enter (+ เป็น optional) ----
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;

      // ตัวเลข
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        const newValue = value === "0" ? key : value + key;
        onChange?.(newValue);
        return;
      }

      // จุดทศนิยม
      if (key === ".") {
        e.preventDefault();
        if (!value.includes(".")) onChange?.(value + ".");
        return;
      }

      // Backspace
      if (key === "Backspace") {
        e.preventDefault();
        const newValue = value.length > 1 ? value.slice(0, -1) : "0";
        onChange?.(newValue);
        return;
      }

      // Delete
      if (key === "Delete") {
        e.preventDefault();
        onChange?.("0");
        return;
      }

      // เครื่องหมายบวก (ถ้าอยากรองรับจากคีย์บอร์ด)
      if (key === "+") {
        e.preventDefault();
        const current = parseFloat(value || "0");
        setAccumulator((prev) => (prev ?? 0) + current);
        setIsAdding(true);
        onChange?.("0");
        return;
      }

      // Enter = submit (รวม accumulator ถ้ามี)
      if (key === "Enter") {
        e.preventDefault();
        const current = parseFloat(value || "0");
        const total = (accumulator ?? 0) + current;
        onSubmit?.(total);
        setAccumulator(null);
        setIsAdding(false);
        onChange?.("0");
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [value, accumulator, isAdding, onChange, onSubmit]);

  // ---- Click handlers ของปุ่ม ----
  const handleClick = (val) => {
    if (val === "ล้าง") {
      onClear?.();
      setAccumulator(null);
      setIsAdding(false);
      return;
    }
    if (val === "+") {
      const current = parseFloat(value || "0");
      setAccumulator((prev) => (prev ?? 0) + current);
      setIsAdding(true);
      onChange?.("0");
      return;
    }
    if (val === "OK") {
      const current = parseFloat(value || "0");
      const total = (accumulator ?? 0) + current;
      onSubmit?.(total);
      setAccumulator(null);
      setIsAdding(false);
      onChange?.("0");
      return;
    }
    if (val === ".") {
      if (value.includes(".")) return;
    }
    const newValue = value === "0" && val !== "." ? String(val) : String(value) + String(val);
    onChange?.(newValue);
  };

  const renderButton = (val, options = {}) => {
    const isClear = val === "ล้าง";
    const isOK = val === "OK";

    return (
      <Button
        key={`key-${val}-${options.gridRow || 1}-${options.gridColumn || 1}`}
        onClick={() => handleClick(val)}
        variant="contained"
        sx={{
          height: options.height || 60,
          backgroundColor: isClear ? "#fce0e0" : isOK ? "#333" : "#eae4f2",
          color: isOK ? "#fff" : "#000",
          border: isOK ? "2px solid #111" : "1px solid #ccc",
          borderRadius: 2,
          fontWeight: "bold",
          fontSize: "1rem",
          minWidth: 0,
          p: 0,
          gridColumn: options.gridColumn || "span 1",
          gridRow: options.gridRow || "span 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {val === "ล้าง" ? <BackspaceIcon /> : val}
      </Button>
    );
  };

  return (
    <Box sx={{ width: 280, bgcolor: "#f0f8ff", p: 2, borderRadius: 2, boxShadow: 1 }}>
      {/* Display */}
      <Box
        bgcolor="#000"
        color="#fff"
        p={1}
        borderRadius={1}
        textAlign="right"
        sx={{ mb: 2, height: 40, display: "flex", alignItems: "center", justifyContent: "flex-end" }}
      >
        <Typography fontSize="1.3rem" fontWeight="bold">
          {value === "." || value === "" ? "0." : Number(value).toLocaleString()}
        </Typography>
      </Box>

      {/* Keypad Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px" }}>
        {renderButton("7")}
        {renderButton("8")}
        {renderButton("9")}
        {renderButton("ล้าง")}

        {renderButton("4")}
        {renderButton("5")}
        {renderButton("6")}
        {renderButton("+", { gridRow: "span 2", height: 128 })}

        {renderButton("1")}
        {renderButton("2")}
        {renderButton("3")}

        {renderButton("0")}
        {renderButton(".")}

        {renderButton("OK", { gridColumn: "span 2" })}
      </Box>

      {/* ปุ่มเอาค่าน้ำหนักจากเครื่องชั่ง */}
      <Box mt={2}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={{ height: 45 }}
          onClick={() => {
            if (weight && parseFloat(weight) !== 0) {
              onChange?.(String(weight));
            }
          }}
        >
          <ScaleIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default Keypad;
